import { TreeData, TreeDataNode } from '@livv/models';
import { Box, Card, List, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import ParentNode from '@screens/Books/Book/Nodes/Previews/ParentNode';
import { useBookPageContext } from '@utils/context/book';
import { useBookPathSlugs } from '@utils/hooks/useBookSlugs';

type ParentNodeData = {
    parentNodes?: TreeDataNode[];
    nodePublished?: boolean;
};

const getParentNodeData = (
    bookData: TreeData,
    bookPath: string,
    parentPath?: string[],
): ParentNodeData => {
    if (parentPath) {
        const { children, isPublished } = bookData.getNode(parentPath);

        return { nodePublished: isPublished, parentNodes: children };
    }
    const { content } = bookData;
    const node = content.find(({ id }) => id === bookPath);

    return { nodePublished: node?.isPublished, parentNodes: content };
};

const ParentNodes: FC = () => {
    const { bookData } = useBookPageContext();
    const { bookId, bookPath } = useBookPathSlugs();
    const router = useRouter();
    const { t } = useTranslation('books');

    const currentPathParts = bookPath.split('/');
    const currentPartId = currentPathParts[currentPathParts.length - 1];
    const parentPath = currentPathParts.length > 1 ? currentPathParts.slice(0, -1) : undefined;

    const { parentNodes, nodePublished } = getParentNodeData(bookData, bookPath, parentPath);
    const filteredParentNodes = parentNodes?.filter(({ id }) => id !== currentPartId); // Filter out the current part

    if (!nodePublished) {
        router.push('/404');
    }

    if (!filteredParentNodes || filteredParentNodes.length === 0) {
        return null;
    }

    return (
        <Card
            elevation={0}
            sx={{
                backgroundColor: 'background.paper',
                border: 'none',
                mt: 5,
                px: 2,
                py: 3,
            }}
        >
            <Box>
                <Typography data-test-id="parent-nodes-title" variant="h2">
                    {t('relatedLegalConcepts')}
                </Typography>
                <List sx={{ my: 2, pl: 5 }} disablePadding>
                    {filteredParentNodes.map(({ id, title }) => (
                        <ParentNode
                            key={id}
                            bookId={bookId}
                            bookPath={bookPath}
                            id={id}
                            parentPath={parentPath}
                            title={title}
                        />
                    ))}
                </List>
            </Box>
        </Card>
    );
};

export default ParentNodes;
